import { ref, toRef, watch } from 'vue';
import { RouteLocation, Router } from 'vue-router';

import { useCatalogFilter } from '~/services/catalogFilter/filter';
import { useSizesFilter } from '~/services/catalogFilter/sizes';
import { useBrands } from '~/services/catalogFilter/brands';
import {
  useAvailableFilters, useStateAttributes,
  useStateAvailableFilters,
} from '~/services/catalogFilter/useAvailableFilters';
import { useProductConditions } from '~/services/catalogFilter/productConditions';
import { useState } from '#app';

export const useFilters = () => useState('useFilters');
export const useFiltersByAttributes = () => useState('useFiltersByAttributes');

export function useCatalogFilters(route: RouteLocation, router: Router) {
  const { t } = useI18n();
  const { categoryAvailableSizes } = useSizesFilter();
  const { allBrands } = useBrands();
  const { allSellerTypes, availableSellerTypes } = useAvailableFilters(route);
  const { allProductConditions } = useProductConditions();
  const availableFilters = useStateAvailableFilters();
  const attributes = useStateAttributes();

  const filters = useFilters();
  const filtersByAttributes = useFiltersByAttributes();

  watch(() => attributes.value, (newAttributes) => {
    const filtersAttrs = {};

    newAttributes?.forEach((attribute) => {
      filtersAttrs[attribute.id] = {
        ...attribute,
        ...useCatalogFilter({
          route, router, allValues: toRef(attribute, 'attributeValues'), availableValues: toRef(availableFilters.value, 'filter'), queryParamName: 'filter',
        }),
      };
    });

    filtersByAttributes.value = filtersAttrs;
  }, { immediate: true });

  function setFilters() {
    filters.value = {
      brand: {
        ...useCatalogFilter({
          route,
          router,
          allValues: allBrands,
          availableValues: toRef(availableFilters.value, 'brand'),
          queryParamName: 'brand',
        }),
        hasSearchField: true,
        searchFiledPlaceHolder: t('services.catalogFilter.useCatalogFilters.searchFiledPlaceHolder'),
      },
      size: useCatalogFilter({
        route,
        router,
        allValues: categoryAvailableSizes,
        availableValues: toRef(availableFilters.value, 'size'),
        queryParamName: 'size',
      }),
      productCondition: useCatalogFilter({
        route,
        router,
        allValues: allProductConditions,
        availableValues: toRef(availableFilters.value, 'productCondition'),
        queryParamName: 'productCondition',
      }),
      isPro: useCatalogFilter({
        route, router, allValues: ref(allSellerTypes), availableValues: availableSellerTypes, queryParamName: 'isPro',
      }),
    };
  }

  setFilters();

  return {
    setFilters,
  };
}
